import React, { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Box, Card, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import AppIcon from '../components/common/AppIcon';
import WelcomeBanner from '../components/dashboard/WelcomeBanner';
import SummaryCard from '../components/dashboard/SummaryCard';
import UpcomingTasks from '../components/dashboard/UpcomingTasks';
import LoadingSpinner from '../components/common/LoadingSpinner';
import usePageTitle from '../hooks/usePageTitle';
import { Wedding, getWedding } from '../services/wedding';
import { DashboardData, getDashboard } from '../services/dashboard';
import TaskForm from '../components/tasks/TaskForm';
import { Task, TaskFormData, getTasks, updateTask } from '../services/tasks';
import { Topic, getTopics } from '../services/topics';
import { Planner, getPlanners } from '../services/planners';
import confetti from 'canvas-confetti';
import { useCurrency } from '../context/CurrencyContext';
import { AppOutletContext } from '../components/layout/AppLayout';

const DashboardPage: React.FC = () => {
  usePageTitle('Dashboard');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { symbol: currencySymbol } = useCurrency();
  const { refreshKey } = useOutletContext<AppOutletContext>();
  const [wedding, setWedding] = useState<Wedding | null>(null);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [wRes, dRes, tRes, pRes, topRes] = await Promise.all([
          getWedding(),
          getDashboard(),
          getTasks(),
          getPlanners(),
          getTopics(),
        ]);
        setWedding(wRes.data);
        if (wRes.data.onboarding_completed === false) {
          navigate('/onboarding', { replace: true });
          return;
        }
        setDashboard(dRes.data);
        setPlanners(pRes.data);
        setAllTasks(tRes.data);
        setTopics(topRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refreshKey]);

  if (loading || !wedding || !dashboard) return <LoadingSpinner />;

  const { task_summary, topic_summary, budget_summary, guest_summary } = dashboard;
  const totalBudget = budget_summary.total_budget;
  const paidPercent = totalBudget > 0 ? Math.min((budget_summary.total_paid / totalBudget) * 100, 100) : 0;
  const weddingDayPercent = totalBudget > 0 ? Math.min((budget_summary.total_due_on_wedding_day / totalBudget) * 100, 100 - paidPercent) : 0;

  const completeTask = async (task: Task, x: number, y: number) => {
    // Optimistic update — remove from active lists immediately
    setAllTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'completed' as const } : t));
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { x: x / window.innerWidth, y: y / window.innerHeight },
      colors: ['#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E'],
      zIndex: 1400,
    });
    await updateTask(task.id, { status: 'completed' });
    const [dRes, tRes] = await Promise.all([getDashboard(), getTasks()]);
    setDashboard(dRes.data);
    setAllTasks(tRes.data);
  };

  const handleViewTask = (task: Task) => {
    setEditingTask(task);
    setFormOpen(true);
  };

  const handleSaveTask = async (data: TaskFormData) => {
    if (!editingTask) return;
    try {
      await updateTask(editingTask.id, data);
      setFormOpen(false);
      setEditingTask(null);
      const [dRes, tRes] = await Promise.all([getDashboard(), getTasks()]);
      setDashboard(dRes.data);
      setAllTasks(tRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const remaining = task_summary.total - task_summary.completed;
  const taskSubtitle = [
    `${task_summary.in_progress} in progress`,
    task_summary.overdue > 0 ? `${task_summary.overdue} overdue` : '',
  ].filter(Boolean).join(', ');

  return (
    <>
      <WelcomeBanner wedding={wedding} />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <SummaryCard
            icon={<AppIcon name="task-alt" />}
            title="Tasks"
            value={`${remaining} remaining`}
            subtitle={taskSubtitle}
            color="#6A8EB0"
            onClick={() => navigate('/tasks')}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          {isMobile ? (
            <Card
              onClick={() => navigate('/topics')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 1.5,
                height: '100%',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                boxShadow: 'none',
                bgcolor: 'background.paper',
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.1, mb: 0.5 }}>
                {topic_summary.total - topic_summary.covered}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#B07A8F' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '& > *': { width: '18px !important', height: '18px !important' }, '& svg': { width: 18, height: 18 } }}>
                  <AppIcon name="topic" sx={{ fontSize: 18, color: '#B07A8F' }} />
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                  Topics
                </Typography>
              </Box>
            </Card>
          ) : (
            <Card
              onClick={() => navigate('/topics')}
              sx={{
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                p: 2,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: 'linear-gradient(90deg, #B07A8F 0%, #B07A8F80 100%)',
                },
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(176,122,143,0.10)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#B07A8F20',
                    color: '#B07A8F',
                    flexShrink: 0,
                    fontSize: 20,
                  }}
                >
                  📋
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">Topics</Typography>
                  <Typography variant="h5" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
                    {topic_summary.total - topic_summary.covered} remaining
                  </Typography>
                  <Box sx={{ position: 'relative', height: 6, borderRadius: 3, mt: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
                    <Box sx={{
                      position: 'absolute', left: 0, top: 0, height: '100%',
                      width: topic_summary.total > 0 ? `${(topic_summary.covered / topic_summary.total) * 100}%` : '0%',
                      bgcolor: '#B07A8F', borderRadius: 3,
                    }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {topic_summary.covered} / {topic_summary.total} covered
                  </Typography>
                </Box>
              </Box>
            </Card>
          )}
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          {isMobile ? (
            <Card
              onClick={() => navigate('/budget')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 1.5,
                height: '100%',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                boxShadow: 'none',
                bgcolor: 'background.paper',
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.1, mb: 0.5 }}>
                {currencySymbol}{totalBudget.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#6B9E78' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '& > *': { width: '18px !important', height: '18px !important' }, '& svg': { width: 18, height: 18 } }}>
                  <AppIcon name="wallet" sx={{ fontSize: 18, color: '#6B9E78' }} />
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                  Budget
                </Typography>
              </Box>
            </Card>
          ) : (
            <Card
              onClick={() => navigate('/budget')}
              sx={{
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                p: 2,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: 'linear-gradient(90deg, #6B9E78 0%, #6B9E7880 100%)',
                },
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(176,122,143,0.10)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#6B9E7820',
                    color: '#6B9E78',
                    flexShrink: 0,
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  {currencySymbol}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">Budget</Typography>
                  <Typography variant="h5" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
                    {currencySymbol}{totalBudget.toLocaleString()}
                  </Typography>
                  <Box sx={{ position: 'relative', height: 6, borderRadius: 3, mt: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${paidPercent}%`, bgcolor: '#6B9E78', borderRadius: 3 }} />
                    {weddingDayPercent > 0 && (
                      <Box sx={{ position: 'absolute', right: 0, top: 0, height: '100%', width: `${weddingDayPercent}%`, bgcolor: 'warning.main', borderRadius: '0 3px 3px 0' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {currencySymbol}{budget_summary.total_paid.toLocaleString()} paid
                    </Typography>
                    {budget_summary.total_due_on_wedding_day > 0 && (
                      <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                        {currencySymbol}{budget_summary.total_due_on_wedding_day.toLocaleString()} due on wedding day
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          )}
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <SummaryCard
            icon={<AppIcon name="people" />}
            title="Guests"
            value={`${guest_summary.total_expected_to_attend} expected`}
            subtitle={`${guest_summary.total_invited} invited`}
            color="#C9A96E"
            onClick={() => navigate('/guests')}
          />
        </Grid>
      </Grid>
      <UpcomingTasks
        tasks={allTasks}
        planners={planners}
        topics={topics}
        onView={handleViewTask}
        onComplete={async (task, e) => {
          const x = e.clientX;
          const y = e.clientY;
          await completeTask(task, x, y);
        }}
      />
      <TaskForm
        open={formOpen}
        task={editingTask}
        planners={planners}
        topics={topics}
        onSave={handleSaveTask}
        onClose={() => { setFormOpen(false); setEditingTask(null); }}
        onDelete={() => { setFormOpen(false); setEditingTask(null); }}
      />
    </>
  );
};

export default DashboardPage;
